/**
 * Brand/model data layer (see @/data). IMPORTANT
 * The client’s SEO/content datasets are synced into `src/client-content/` via:
 * - `pnpm sync:client-content`
 *
 * This makes the frontend deployable without shipping `/br/`.
 */

// Client-supplied brand data (43 brands with editorial + FAQs + buyer guides)
import type { Brand as ClientBrand } from '@/client-content/(browse)/brands/_lib/brand-data'
import {
  brands as clientBrands,
  getBrandBySlug as getClientBrandBySlug,
  formatPriceCents,
  getGroupedBrands,
} from '@/client-content/(browse)/brands/_lib/brand-data'

// Client-supplied model data (355 models, each with explicit year arrays)
import type { ModelData } from '@/client-content/(browse)/brands/_lib/model-data'
import {
  allModels,
  getModelBySlug,
  getModelsByBrand,
} from '@/client-content/(browse)/brands/_lib/model-data'

export type Brand = ClientBrand
export type { ModelData }

export function getBrands(): Brand[] {
  return clientBrands
}

export function getBrandBySlug(slug: string): Brand | undefined {
  return getClientBrandBySlug(slug)
}

export function getModelsForBrand(brandSlug: string): ModelData[] {
  return getModelsByBrand(brandSlug)
}

export function getBrandModel(brandSlug: string, modelSlug: string): ModelData | undefined {
  return getModelBySlug(brandSlug, modelSlug)
}

export function getBrandModelYears(brandSlug: string, modelSlug: string): number[] {
  const model = getBrandModel(brandSlug, modelSlug)
  return model?.years ?? []
}

export function getSeoPageCounts(): {
  brands: number
  models: number
  yearPages: number
  total: number
  years: number[]
} {
  const brands = clientBrands.length
  const models = allModels.length
  const yearPages = allModels.reduce((sum, m) => sum + m.years.length, 0)
  const years = Array.from(new Set(allModels.flatMap((m) => m.years))).sort((a, b) => b - a)
  return { brands, models, yearPages, total: 1 + brands + models + yearPages, years }
}

export { formatPriceCents, getGroupedBrands }

